import type {
  AdminPipelineRun,
  AuthSession,
  BatchReadResult,
  ConnectorTestResult,
  CopyJob,
  Dataset,
  DatasetDetail,
  DatasetFile,
  DatasetReadiness,
  DatasetObject,
  FsConnector,
  FsConnectorCreate,
  FsConnectorUpdate,
  ColumnMatchResponse,
  EmailProviderConfig,
  EvaluationReport,
  EmailProviderPayload,
  ExpectedFilesListResponse,
  RegisterExpectedFilesRequest,
  RegisterExpectedFilesResponse,
  MasterClinicalArtefactsResponse,
  MasterClinicalDownloadResponse,
  MasterClinicalFormat,
  MasterFilePreviewResponse,
  MaterialiseMasterClinicalRequest,
  MatchingResult,
  ModelCard,
  ModelChecklistItem,
  ModelSpecs,
  Notification,
  PdfGenerationJob,
  NotificationTemplate,
  PipelineRun,
  Project,
  ProjectQcDecisions,
  ProjectQcOverrideRequest,
  QcDecisionRequest,
  QcFlagsResponse,
  QcReport,
  QcReportDetail,
  RemoteFile,
  RunProgress,
  RunSummaryResponse,
  SchemaMatchingResult,
  SftpCredentials,
  UnreadCount,
  UploadUrlRequest,
  UploadUrlResponse,
  UserProfile,
  ValueMappingEntryDto,
  ValueMappingsResponse,
  WizardCheckpoint,

} from './contracts';
import type {
  OperationsBatchCancelPreview,
  OperationsBatchCancelResult,
} from './observability';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

export type RequestAuthContext = {
  accessToken?: string;
  tenantId?: string;
};

/**
 * Typed error thrown by ``request()`` when the backend returns a non-OK
 * response. Carries the HTTP status code so callers can discriminate
 * without resorting to substring matching on the message body — the BFF
 * error envelope uses lowercase codes (``"unauthorized"``, ``"forbidden"``)
 * which a case-sensitive ``String.includes("Unauthorized")`` would miss.
 */
export class ApiError extends Error {
  readonly status: number;
  // PCS-351: surface the BFF/service error envelope's ``code`` field so
  // callers can dispatch on it (e.g. ``schema_mapping_not_found`` vs
  // ``dataset_not_found`` — both 404 but very different recoveries).
  // Optional because pre-existing call sites that only branch on
  // ``status`` keep working unchanged.
  readonly code?: string;
  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

// ── Token refresh coordination ──────────────────────────────────────────────
type AuthCallbacks = {
  getSession: () => AuthSession | undefined;
  onRefresh: (session: AuthSession) => void;
  onRefreshFail: () => void;
};
let _authCallbacks: AuthCallbacks | null = null;
let _refreshPromise: Promise<AuthSession | null> | null = null;

export function registerAuthCallbacks(callbacks: AuthCallbacks): void {
  _authCallbacks = callbacks;
}

export async function attemptRefresh(): Promise<AuthSession | null> {
  if (_refreshPromise) return _refreshPromise;
  const session = _authCallbacks?.getSession();
  const refreshToken = session?.refresh_token;
  if (!refreshToken) {
    _authCallbacks?.onRefreshFail();
    return null;
  }
  _refreshPromise = (async () => {
    try {
      const refreshed = await apiClient.refreshSession(refreshToken);
      const merged: AuthSession = {
        ...refreshed,
        refresh_token: refreshed.refresh_token ?? refreshToken,
        token_obtained_at: Date.now(),
      };
      _authCallbacks?.onRefresh(merged);
      return merged;
    } catch (err) {
      // Only force logout on auth rejection (401/403). Transient server
      // errors (503, network timeouts) and everything else should not clear
      // the session — the next request will retry naturally. Typed status
      // check avoids false negatives from case-sensitive substring matching
      // against a lowercase ``"unauthorized"`` BFF error envelope.
      const isAuthRejection =
        err instanceof ApiError && (err.status === 401 || err.status === 403);
      if (isAuthRejection) {
        _authCallbacks?.onRefreshFail();
      }
      return null;
    } finally {
      _refreshPromise = null;
    }
  })();
  return _refreshPromise;
}

function parseApiError(body: string, status: number): ApiError {
  let message = body || `Request failed with status ${status}`;
  let code: string | undefined;
  try {
    const parsed = JSON.parse(body) as unknown;
    if (parsed && typeof parsed === 'object') {
      // Platform envelope: {"error": {"code", "message", ...}}
      // Flat fallback: {"message": ...} (FastAPI HTTPException default)
      const outer = parsed as { error?: unknown; message?: unknown; code?: unknown };
      const inner =
        outer.error && typeof outer.error === 'object'
          ? (outer.error as { code?: unknown; message?: unknown })
          : undefined;
      const rawMessage = inner?.message ?? outer.message;
      const rawCode = inner?.code ?? outer.code;
      if (typeof rawMessage === 'string') message = rawMessage;
      if (typeof rawCode === 'string') code = rawCode;
    }
  } catch {
    // body is not JSON — use raw text as-is
  }
  return new ApiError(status, message, code);
}

async function requestBlob(
  path: string,
  auth?: RequestAuthContext,
  _isRetry = false,
): Promise<Blob> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    headers: {
      ...(auth?.accessToken ? { Authorization: `Bearer ${auth.accessToken}` } : {}),
      ...(auth?.tenantId ? { 'X-Tenant-Id': auth.tenantId } : {}),
    },
  });

  if (response.status === 401 && auth?.accessToken && !_isRetry) {
    const refreshed = await attemptRefresh();
    if (refreshed) {
      return requestBlob(
        path,
        { accessToken: refreshed.access_token, tenantId: refreshed.tenant_id ?? auth.tenantId },
        true,
      );
    }
  }

  if (!response.ok) {
    throw parseApiError(await response.text(), response.status);
  }
  return response.blob();
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  auth?: RequestAuthContext,
  // Internal: ``true`` on the single retry issued after a successful token
  // refresh. Prevents an unbounded 401 → refresh → 401 loop when the API
  // keeps rejecting for a non-token reason (account disabled, permission
  // revoked, backend validation mismatch) while the IdP refresh endpoint
  // keeps succeeding.
  _isRetry = false,
): Promise<T> {
  const isFormData = options.body instanceof FormData;
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...(auth?.accessToken ? { Authorization: `Bearer ${auth.accessToken}` } : {}),
      ...(auth?.tenantId ? { 'X-Tenant-Id': auth.tenantId } : {}),
      ...(options.headers ?? {}),
    },
  });

  // Reactive refresh on 401 — skip for auth endpoints (the refresh endpoint
  // cannot refresh itself) and skip if we've already retried once.
  if (
    response.status === 401 &&
    auth?.accessToken &&
    !path.startsWith('/auth/') &&
    !_isRetry
  ) {
    const refreshed = await attemptRefresh();
    if (refreshed) {
      return request<T>(
        path,
        options,
        {
          accessToken: refreshed.access_token,
          tenantId: refreshed.tenant_id ?? auth.tenantId,
        },
        true,
      );
    }
  }

  if (!response.ok) {
    throw parseApiError(await response.text(), response.status);
  }

  if (response.status === 204) {
    return {} as T;
  }

  return response.json() as Promise<T>;
}

export const apiClient = {
  login: (username: string, password: string, tenantId: string) =>
    request<AuthSession>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password, tenant_id: tenantId }),
    }),
  respondToChallenge: (username: string, newPassword: string, session: string) =>
    request<AuthSession>('/auth/challenge', {
      method: 'POST',
      body: JSON.stringify({ username, new_password: newPassword, session }),
    }),
  refreshSession: (refreshToken: string) =>
    request<AuthSession>('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
    }),
  logout: (auth?: RequestAuthContext) => request<void>('/me', { method: 'DELETE' }, auth),
  getMe: (auth?: RequestAuthContext) => request<UserProfile>('/me', {}, auth),
  listProjects: (auth?: RequestAuthContext) => request<Project[]>('/projects', {}, auth),
  createProject: (
    payload: { name: string; model_id?: string; dataset_id?: string; creation_source?: string },
    auth?: RequestAuthContext,
  ) =>
    request<Project>(
      '/projects',
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      auth,
    ),
  renameProject: (projectId: string, name: string, auth?: RequestAuthContext) =>
    request<Project>(
      `/projects/${projectId}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ name }),
      },
      auth,
    ),
  listDatasets: (auth?: RequestAuthContext) => request<Dataset[]>('/datasets', {}, auth),
  getDataset: (datasetId: string, auth?: RequestAuthContext) =>
    request<DatasetDetail>(`/datasets/${datasetId}`, {}, auth),
  getDatasetReadiness: (datasetId: string, auth?: RequestAuthContext) =>
    request<DatasetReadiness>(`/datasets/${datasetId}/readiness`, {}, auth),
  createDataset: (
    name: string,
    transfer_method: 'browser' | 'sftp' = 'browser',
    auth?: RequestAuthContext,
  ) =>
    request<Dataset>(
      '/datasets',
      {
        method: 'POST',
        body: JSON.stringify({ name, transfer_method }),
      },
      auth,
    ),
  uploadDatasetFile: (
    datasetId: string,
    payload: {
      file_kind: string;
      object_uri: string;
      original_name: string;
      relative_path?: string | null;
    },
    auth?: RequestAuthContext,
  ) =>
    request<DatasetFile>(
      `/datasets/${datasetId}/files`,
      { method: 'POST', body: JSON.stringify(payload) },
      auth,
    ),
  /**
   * Fetch per-tenant/per-dataset SFTP credentials (PCS-279). Returns 404
   * when the dataset is not owned by the caller's tenant. Call this on
   * demand — never cache the response in localStorage or similar.
   */
  getDatasetSftpCredentials: (datasetId: string, auth?: RequestAuthContext) =>
    request<SftpCredentials>(`/datasets/${datasetId}/sftp-access`, {}, auth),
  /**
   * PCS-238: register the pre-upload manifest (file list + sizes the
   * user scanned locally). Backend persists `status='expected'` rows;
   * a 409 means a manifest already exists — re-call with
   * `overwrite: true` to replace.
   */
  registerExpectedFiles: (
    datasetId: string,
    payload: RegisterExpectedFilesRequest,
    auth?: RequestAuthContext,
  ) =>
    request<RegisterExpectedFilesResponse>(
      `/datasets/${datasetId}/expected-files`,
      { method: 'POST', body: JSON.stringify(payload) },
      auth,
    ),
  /**
   * PCS-238: fetch the dataset's manifest with reconciliation counts.
   * Empty manifest returns `total: 0` (200, not 404) — distinguishes
   * "not registered yet" from "dataset not found".
   */
  listExpectedFiles: (datasetId: string, auth?: RequestAuthContext) =>
    request<ExpectedFilesListResponse>(
      `/datasets/${datasetId}/expected-files`,
      {},
      auth,
    ),
  /**
   * Request a pre-signed PUT URL for a browser upload (PCS-280). The
   * browser uploads the binary to `upload_url` directly (bypassing the
   * BFF for WSI files up to 5 GB), then calls `uploadDatasetFile` with
   * the returned `object_uri` to register the slide.
   */
  requestDatasetUploadUrl: (
    datasetId: string,
    payload: UploadUrlRequest,
    auth?: RequestAuthContext,
  ) =>
    request<UploadUrlResponse>(
      `/datasets/${datasetId}/upload-url`,
      { method: 'POST', body: JSON.stringify(payload) },
      auth,
    ),
  /**
   * Signal that the user is done uploading slides for this dataset.
   * Flips backend `ingestion_status` from `pending` to `ready`, which
   * is the gate the slide-hasher's feature-extraction auto-trigger
   * waits on. Idempotent — safe to call more than once.
   */
  finalizeDatasetUpload: (datasetId: string, auth?: RequestAuthContext) =>
    request<Dataset>(
      `/datasets/${datasetId}/finalize-upload`,
      { method: 'POST' },
      auth,
    ),
  /**
   * PCS-538 — finalize a stuck *manifest* dataset whose promised files
   * never all arrived. Promotes the remaining `expected` rows to `missing`
   * and flips `ingestion_status` to `ready` so analysis can proceed.
   * Distinct from `finalizeDatasetUpload` (the manifest-free "that's all").
   */
  finalizeExpectedFilesAnyway: (datasetId: string, auth?: RequestAuthContext) =>
    request<Dataset>(
      `/datasets/${datasetId}/expected-files/finalize-anyway`,
      { method: 'POST' },
      auth,
    ),
  listModels: (options?: { includeDraft?: boolean }, auth?: RequestAuthContext) =>
    request<ModelCard[]>(options?.includeDraft === false ? '/models?include_draft=false' : '/models', {}, auth),
  addModel: (payload: { name: string; version: string; status: string; metadata: Record<string, string>; specs?: ModelSpecs | null; uri?: string | null }, auth?: RequestAuthContext) =>
    request<ModelCard>(
      '/models',
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      auth,
    ),
  updateModel: (
    modelId: string,
    payload: { name: string; version: string; status: string; metadata: Record<string, string>; specs?: ModelSpecs | null; uri?: string | null },
    auth?: RequestAuthContext,
  ) =>
    request<ModelCard>(
      `/models/${modelId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(payload),
      },
      auth,
    ),
  renameDataset: (datasetId: string, name: string, auth?: RequestAuthContext) =>
    request<Dataset>(`/datasets/${datasetId}`, { method: 'PATCH', body: JSON.stringify({ name }) }, auth),
  deleteModel: (modelId: string, auth?: RequestAuthContext) =>
    request<void>(`/models/${modelId}`, { method: 'DELETE' }, auth),
  getModelChecklist: (modelId: string, auth?: RequestAuthContext) =>
    request<ModelChecklistItem[]>(`/models/${modelId}/checklist`, {}, auth),
  updateModelChecklist: (modelId: string, items: Omit<ModelChecklistItem, 'model_id'>[], auth?: RequestAuthContext) =>
    request<ModelChecklistItem[]>(`/models/${modelId}/checklist`, { method: 'PUT', body: JSON.stringify(items) }, auth),
  deleteProject: (projectId: string, auth?: RequestAuthContext) =>
    request<void>(`/projects/${projectId}`, { method: 'DELETE' }, auth),
  deleteDataset: (datasetId: string, auth?: RequestAuthContext) =>
    request<void>(`/datasets/${datasetId}`, { method: 'DELETE' }, auth),
  listNotifications: (auth?: RequestAuthContext) => request<Notification[]>('/notifications', {}, auth),
  markNotificationRead: (notificationId: string, auth?: RequestAuthContext) =>
    request<Notification>(`/notifications/${notificationId}/read`, { method: 'POST' }, auth),
  batchSetRead: (notificationIds: string[], isRead: boolean, auth?: RequestAuthContext) =>
    request<BatchReadResult>(
      '/notifications/batch-read',
      { method: 'POST', body: JSON.stringify({ notification_ids: notificationIds, is_read: isRead }) },
      auth,
    ),
  getUnreadCount: (auth?: RequestAuthContext) => request<UnreadCount>('/notifications/unread-count', {}, auth),
  listNotificationTemplates: (auth?: RequestAuthContext) =>
    request<NotificationTemplate[]>('/notifications/templates', {}, auth),
  createNotificationTemplate: (
    payload: { key: string; channel: string; subject: string; body: string },
    auth?: RequestAuthContext,
  ) =>
    request<NotificationTemplate>(
      '/notifications/templates',
      { method: 'POST', body: JSON.stringify(payload) },
      auth,
    ),
  deleteNotificationTemplate: (key: string, auth?: RequestAuthContext) =>
    request<void>(`/notifications/templates/${encodeURIComponent(key)}`, { method: 'DELETE' }, auth),
  listEmailProviders: (auth?: RequestAuthContext) =>
    request<EmailProviderConfig[]>('/admin/notifications/providers', {}, auth),
  createEmailProvider: (payload: EmailProviderPayload, auth?: RequestAuthContext) =>
    request<EmailProviderConfig>(
      '/admin/notifications/providers',
      { method: 'POST', body: JSON.stringify(payload) },
      auth,
    ),
  updateEmailProvider: (providerId: string, payload: EmailProviderPayload, auth?: RequestAuthContext) =>
    request<EmailProviderConfig>(
      `/admin/notifications/providers/${providerId}`,
      { method: 'PUT', body: JSON.stringify(payload) },
      auth,
    ),
  deleteEmailProvider: (providerId: string, auth?: RequestAuthContext) =>
    request<void>(`/admin/notifications/providers/${providerId}`, { method: 'DELETE' }, auth),
  testEmailProvider: (providerId: string, auth?: RequestAuthContext) =>
    request<{ status: string; detail?: string }>(
      `/admin/notifications/providers/${providerId}/test`,
      { method: 'POST' },
      auth,
    ),
  listRuns: (auth?: RequestAuthContext) => request<PipelineRun[]>('/runs', {}, auth),
  createRun: (payload: { project_id: string; model_id?: string | null }, auth?: RequestAuthContext) =>
    request<PipelineRun>('/runs', { method: 'POST', body: JSON.stringify(payload) }, auth),
  advanceRun: (
    runId: string,
    stagePayload?: Record<string, unknown>,
    auth?: RequestAuthContext,
  ) =>
    request<PipelineRun>(
      `/runs/${runId}/advance`,
      {
        method: 'POST',
        body: JSON.stringify({ payload: stagePayload ?? {} }),
      },
      auth,
    ),
  listRunsAdmin: (status?: string, auth?: RequestAuthContext) =>
    request<AdminPipelineRun[]>(
      `/runs/admin${status ? `?status=${encodeURIComponent(status)}` : ''}`,
      {},
      auth,
    ),
  cancelRun: (runId: string, auth?: RequestAuthContext) =>
    request<PipelineRun>(`/runs/${runId}/cancel`, { method: 'POST' }, auth),
  getRunProgress: (runId: string, auth?: RequestAuthContext) =>
    request<RunProgress>(`/runs/${runId}/progress`, {}, auth),

  deleteDatasetObjects: (datasetId: string, keys: string[], auth?: RequestAuthContext) =>
    request<{ deleted: number }>(
      `/datasets/${datasetId}/objects/delete`,
      { method: 'POST', body: JSON.stringify({ keys }) },
      auth,
    ),
  listDatasetObjects: (datasetId: string, prefix?: string, auth?: RequestAuthContext) =>
    request<DatasetObject[]>(
      `/datasets/${datasetId}/objects${prefix ? `?prefix=${encodeURIComponent(prefix)}` : ''}`,
      {},
      auth,
    ),

  // ── Filesystem connectors ──────────────────────────────────────────────────
  listConnectors: (auth?: RequestAuthContext) =>
    request<FsConnector[]>('/connectors', {}, auth),
  createConnector: (payload: FsConnectorCreate, auth?: RequestAuthContext) =>
    request<FsConnector>('/connectors', { method: 'POST', body: JSON.stringify(payload) }, auth),
  updateConnector: (connectorId: string, payload: FsConnectorUpdate, auth?: RequestAuthContext) =>
    request<FsConnector>(`/connectors/${connectorId}`, { method: 'PATCH', body: JSON.stringify(payload) }, auth),
  deleteConnector: (connectorId: string, auth?: RequestAuthContext) =>
    request<void>(`/connectors/${connectorId}`, { method: 'DELETE' }, auth),
  testConnector: (connectorId: string, auth?: RequestAuthContext) =>
    request<ConnectorTestResult>(`/connectors/${connectorId}/test`, { method: 'POST' }, auth),
  browseConnector: (connectorId: string, path: string, auth?: RequestAuthContext) =>
    request<RemoteFile[]>(
      `/connectors/${connectorId}/browse${path ? `?path=${encodeURIComponent(path)}` : ''}`,
      {},
      auth,
    ),
  startCopyJob: (
    connectorId: string,
    payload: { remote_paths: string[]; dataset_id: string },
    auth?: RequestAuthContext,
  ) =>
    request<CopyJob>(`/connectors/${connectorId}/copy-jobs`, { method: 'POST', body: JSON.stringify(payload) }, auth),
  getCopyJob: (connectorId: string, jobId: string, auth?: RequestAuthContext) =>
    request<CopyJob>(`/connectors/${connectorId}/copy-jobs/${jobId}`, {}, auth),
  listCopyJobs: (active?: boolean, auth?: RequestAuthContext) =>
    request<CopyJob[]>(`/copy-jobs${active ? '?active=true' : ''}`, {}, auth),

  // ── PCS-249: wizard backend (snapshot, qc, summary, master-file, value mappings) ──
  getCheckpoint: (runId: string, auth?: RequestAuthContext) =>
    request<WizardCheckpoint>(`/runs/${runId}/checkpoint`, {}, auth),
  /** PATCH a partial wizard snapshot. Backend validates against
   *  `WizardSnapshotPartial` (extra='forbid'); fields not in the
   *  partial are preserved. */
  updateCheckpoint: (
    runId: string,
    partial: Record<string, unknown>,
    auth?: RequestAuthContext,
  ) =>
    request<WizardCheckpoint>(
      `/runs/${runId}/payload`,
      { method: 'PATCH', body: JSON.stringify({ payload: partial }) },
      auth,
    ),
  getQcFlags: (runId: string, auth?: RequestAuthContext) =>
    request<QcFlagsResponse>(`/runs/${runId}/qc/flags`, {}, auth),
  getRunSummary: (runId: string, auth?: RequestAuthContext) =>
    request<RunSummaryResponse>(`/runs/${runId}/summary`, {}, auth),
  getRunEvaluation: (runId: string, auth?: RequestAuthContext) =>
    request<EvaluationReport>(`/runs/${runId}/evaluation`, {}, auth),
  getMasterFilePreview: (
    datasetId: string,
    projectId: string,
    auth?: RequestAuthContext,
  ) =>
    request<MasterFilePreviewResponse>(
      `/datasets/${datasetId}/master-file/preview?project_id=${encodeURIComponent(projectId)}`,
      {},
      auth,
    ),
  /** PCS-342 — kick off master-clinical materialisation. Synchronous on
   *  the backend; resolves once ``master_clinical.{csv,xlsx,meta.json}``
   *  are persisted to S3. The wizard re-fires this on format-switch when
   *  the requested format isn't in ``available_formats`` yet. */
  materialiseMasterClinical: (
    projectId: string,
    body: MaterialiseMasterClinicalRequest,
    auth?: RequestAuthContext,
  ) =>
    request<MasterClinicalArtefactsResponse>(
      `/projects/${projectId}/clinical/master`,
      { method: 'POST', body: JSON.stringify(body) },
      auth,
    ),
  /** PCS-342 — fetch a signed download URL. Returns ``null`` ONLY on
   *  404 ``master_clinical_not_materialised`` so the caller can fire
   *  the POST counterpart; other errors propagate. */
  getMasterClinicalDownload: async (
    projectId: string,
    format: MasterClinicalFormat,
    auth?: RequestAuthContext,
  ): Promise<MasterClinicalDownloadResponse | null> => {
    try {
      return await request<MasterClinicalDownloadResponse>(
        `/projects/${projectId}/clinical/master?format=${encodeURIComponent(format)}`,
        {},
        auth,
      );
    } catch (err) {
      if (
        err instanceof ApiError &&
        err.status === 404 &&
        err.code === 'master_clinical_not_materialised'
      ) {
        return null;
      }
      throw err;
    }
  },
  getValueMappings: (
    datasetId: string,
    projectId: string,
    auth?: RequestAuthContext,
  ) =>
    request<ValueMappingsResponse>(
      `/datasets/${datasetId}/clinical/value-mappings?project_id=${encodeURIComponent(projectId)}`,
      {},
      auth,
    ),
  /** Read the persisted column → schema mapping. Returns ``null`` ONLY
   *  when the matcher has not yet run for the project (404
   *  ``schema_mapping_not_found``); the caller dispatches the POST
   *  counterpart in that case. Other 404 codes (``dataset_not_found``,
   *  ``project_not_found``) are rethrown so the caller surfaces an
   *  error banner rather than rendering MOCK rows. PCS-351. */
  getClinicalColumnMatch: async (
    datasetId: string,
    projectId: string,
    auth?: RequestAuthContext,
  ): Promise<SchemaMatchingResult | null> => {
    try {
      return await request<SchemaMatchingResult>(
        `/datasets/${datasetId}/clinical/column-match?project_id=${encodeURIComponent(projectId)}`,
        {},
        auth,
      );
    } catch (err) {
      if (
        err instanceof ApiError &&
        err.status === 404 &&
        err.code === 'schema_mapping_not_found'
      ) {
        return null;
      }
      throw err;
    }
  },
  /** Read the persisted slide↔patient match. Returns ``null`` ONLY on
   *  404 ``clinical_match_not_found``; other 404s (dataset / project
   *  vanished) are rethrown so the caller surfaces an error. PCS-351. */
  getClinicalMatch: async (
    datasetId: string,
    projectId: string,
    auth?: RequestAuthContext,
  ): Promise<MatchingResult | null> => {
    try {
      return await request<MatchingResult>(
        `/datasets/${datasetId}/clinical/match?project_id=${encodeURIComponent(projectId)}`,
        {},
        auth,
      );
    } catch (err) {
      if (
        err instanceof ApiError &&
        err.status === 404 &&
        err.code === 'clinical_match_not_found'
      ) {
        return null;
      }
      throw err;
    }
  },
  /** Persist the user-edited column → schema mapping (PCS-166 PUT).
   *  Full replacement — caller sends every row back, not a patch. Flips
   *  ``schema_mapping_source`` to ``manual`` server-side. */
  putClinicalColumnMatch: (
    datasetId: string,
    payload: {
      project_id: string;
      mappings: Array<{
        standard_key: string;
        standard_label: string;
        dtype: string;
        your_column: string | null;
        status: string;
        suggestion: string | null;
      }>;
      unmatched_input_columns: string[];
    },
    auth?: RequestAuthContext,
  ) =>
    request<SchemaMatchingResult>(
      `/datasets/${datasetId}/clinical/column-match`,
      { method: 'PUT', body: JSON.stringify(payload) },
      auth,
    ),
  /** Pass `force=true` to overwrite an existing manual mapping
   *  (otherwise the dataset_service returns 409). */
  putValueMappings: (
    datasetId: string,
    payload: { project_id: string; entries: ValueMappingEntryDto[] },
    options: { force?: boolean } = {},
    auth?: RequestAuthContext,
  ) =>
    request<ValueMappingsResponse>(
      `/datasets/${datasetId}/clinical/value-mappings${options.force ? '?force=true' : ''}`,
      { method: 'PUT', body: JSON.stringify(payload) },
      auth,
    ),
  /** Run the clinical-schema (column) matcher (PCS-166 / PCS-336).
   *  Returns the persisted artefact's shape directly — wizard's
   *  post-upload card renders `row_count`, `null_count`, and detected
   *  columns from this response without a follow-up GET.
   */
  matchClinicalSchema: (
    datasetId: string,
    projectId: string,
    options: {
      provider?: 'mock' | 'heuristic' | 'ollama' | 'bedrock';
      force?: boolean;
    } = {},
    auth?: RequestAuthContext,
  ) => {
    const params = new URLSearchParams();
    if (options.force) params.set('force', 'true');
    const query = params.toString();
    const body: Record<string, string> = { project_id: projectId };
    if (options.provider) body.provider = options.provider;
    return request<ColumnMatchResponse>(
      `/datasets/${datasetId}/clinical/column-match${query ? `?${query}` : ''}`,
      { method: 'POST', body: JSON.stringify(body) },
      auth,
    );
  },
  /** Run the slide↔patient matcher (PCS-170). Wizard sub-step 3 fires
   *  this when ``getClinicalMatch`` returns ``null`` (no prior run).
   *  Response is the same ``MatchingResult`` shape as the GET, so the
   *  caller renders directly off this without a follow-up fetch. */
  matchClinical: (
    datasetId: string,
    projectId: string,
    options: {
      provider?: 'heuristic' | 'column' | 'ollama' | 'bedrock';
      force?: boolean;
    } = {},
    auth?: RequestAuthContext,
  ) => {
    const params = new URLSearchParams();
    if (options.force) params.set('force', 'true');
    const query = params.toString();
    const body: Record<string, string> = { project_id: projectId };
    if (options.provider) body.provider = options.provider;
    return request<MatchingResult>(
      `/datasets/${datasetId}/clinical/match${query ? `?${query}` : ''}`,
      { method: 'POST', body: JSON.stringify(body) },
      auth,
    );
  },
  uploadSlideCsv: (
    datasetId: string,
    projectId: string,
    file: File,
    auth?: RequestAuthContext,
  ) => {
    const body = new FormData();
    body.append('project_id', projectId);
    body.append('file', file, file.name);
    return request<MatchingResult>(
      `/datasets/${datasetId}/clinical/slide-csv`,
      { method: 'POST', body },
      auth,
    );
  },
  /** PCS-355 — trigger a QC run for a dataset.
   *  Returns the newly-created ``pending`` report.
   *  Callers must handle ``ApiError`` with ``code === 'hash_in_progress'``
   *  (409) — it means hashing is still in flight; show the waiting screen
   *  and poll ``listQcReports`` until a completed report appears. */
  requestQc: (datasetId: string, auth?: RequestAuthContext) =>
    request<QcReport>(`/datasets/${datasetId}/qc`, { method: 'POST' }, auth),

  /** PCS-355 — list all QC reports for a dataset, newest first.
   *  Returns the slim ``QcReport`` shape (no per-slide flags).
   *  Poll this while ``status`` is ``pending`` or ``running``. */
  listQcReports: (datasetId: string, auth?: RequestAuthContext) =>
    request<QcReport[]>(`/datasets/${datasetId}/qc-reports`, {}, auth),

  /** PCS-355 — download the QC flag list as CSV.
   *  Returns a ``Blob``; caller creates an object URL and triggers a
   *  browser download.  Report must be ``completed`` (backend returns 422
   *  ``qc_report_not_ready`` otherwise). */
  downloadQcCsv: (datasetId: string, reportId: string, projectId: string, auth?: RequestAuthContext) =>
    requestBlob(`/datasets/${datasetId}/qc-reports/${reportId}/entries.csv?project_id=${encodeURIComponent(projectId)}`, auth),

  /** PCS-355 — download the full QC report as PDF (synchronous, SUPERSEDED).
   *  Kept for backward-compatibility; prefer ``createQcPdfJob`` for large cohorts. */
  downloadQcPdf: (datasetId: string, reportId: string, projectId: string, auth?: RequestAuthContext) =>
    requestBlob(
      `/datasets/${datasetId}/qc-reports/${reportId}/pdf?project_id=${encodeURIComponent(projectId)}`,
      auth,
    ),

  /** PCS-358 async — enqueue a PDF generation job; returns the job record (HTTP 201). */
  createQcPdfJob: (datasetId: string, reportId: string, projectId: string, auth?: RequestAuthContext) =>
    request<PdfGenerationJob>(
      `/datasets/${datasetId}/qc-reports/${reportId}/pdf-jobs?project_id=${encodeURIComponent(projectId)}`,
      { method: 'POST' },
      auth,
    ),

  /** PCS-358 async — poll PDF job status; ``status`` ∈ ``{pending, processing, ready, failed}``. */
  getQcPdfJob: (datasetId: string, reportId: string, jobId: string, auth?: RequestAuthContext) =>
    request<PdfGenerationJob>(
      `/datasets/${datasetId}/qc-reports/${reportId}/pdf-jobs/${jobId}`,
      {},
      auth,
    ),

  /** PCS-447 — download a completed QC PDF via the authenticated BFF proxy.
   *  Returns the raw PDF bytes as a Blob. */
  downloadQcPdfJob: (datasetId: string, reportId: string, jobId: string, auth?: RequestAuthContext) =>
    requestBlob(`/datasets/${datasetId}/qc-reports/${reportId}/pdf-jobs/${jobId}/download`, auth),

  /** PCS-355 — fetch a completed QC report with embedded per-slide flags.
   *  Use once ``listQcReports`` shows ``status === 'completed'``.
   *  Replaces the legacy ``getQcFlags`` (pipeline-service proxy) for
   *  dataset-scoped QC. */
  getQcReportDetail: (datasetId: string, reportId: string, auth?: RequestAuthContext) =>
    request<QcReportDetail>(`/datasets/${datasetId}/qc-reports/${reportId}`, {}, auth),

  /** PCS-355 — persist a keep/exclude decision for a single QC flag.
   *  ``flagId`` is ``QcFlag.flag_id`` from ``getQcReportDetail``.
   *  Fire optimistically after updating the wizard snapshot. */
  decideQcFlag: (
    datasetId: string,
    reportId: string,
    flagId: string,
    decision: QcDecisionRequest['decision'],
    auth?: RequestAuthContext,
  ) =>
    request<void>(
      `/datasets/${datasetId}/qc-reports/${reportId}/entries/${flagId}/decision`,
      { method: 'POST', body: JSON.stringify({ decision } satisfies QcDecisionRequest) },
      auth,
    ),

  /** PCS-359: project-scoped clini.csv upload. Multipart `file` +
   *  `project_id`. Replaces the legacy pre-signed-URL + `POST
   *  /datasets/{id}/files` (`file_kind="clinical"`) two-step. The
   *  endpoint writes to
   *  ``s3://<bucket>/<tenant>/project/<id>/clinical/clini.csv`` and
   *  invalidates any stale ``master_clinical.{csv,xlsx,meta.json}``
   *  artefacts (PCS-342) so the next materialise rebuilds from fresh
   *  data. */
  uploadCliniCsv: (
    datasetId: string,
    projectId: string,
    file: File,
    auth?: RequestAuthContext,
  ) => {
    const body = new FormData();
    body.append('project_id', projectId);
    body.append('file', file, file.name);
    return request<{ columns: string[]; row_count: number }>(
      `/datasets/${datasetId}/clinical/clini-csv`,
      { method: 'POST', body },
      auth,
    );
  },
  /** Run the value-mapping matcher (PCS-343). Wizard sub-step 2 fires
   *  this when ``getValueMappings`` returns an empty entries list.
   *  Response is the same ``ValueMappingsResponse`` shape as the GET. */
  matchClinicalValues: (
    datasetId: string,
    projectId: string,
    options: {
      provider?: 'heuristic' | 'bedrock';
      force?: boolean;
    } = {},
    auth?: RequestAuthContext,
  ) => {
    const params = new URLSearchParams();
    if (options.force) params.set('force', 'true');
    const query = params.toString();
    const body: Record<string, string> = { project_id: projectId };
    if (options.provider) body.provider = options.provider;
    return request<ValueMappingsResponse>(
      `/datasets/${datasetId}/clinical/value-match${query ? `?${query}` : ''}`,
      { method: 'POST', body: JSON.stringify(body) },
      auth,
    );
  },

  // ── PCS-397: project-scoped QC overrides ──────────────────────────────────

  /** PCS-532 — merged QC review queue plus cohort-scoped tile counts in one
   *  round-trip. Read ``.decisions`` for the per-flag queue and
   *  ``.total``/``.passed``/``.pending_review``/``.excluded`` for the tiles
   *  (NOT ``listQcReports[0].summary``, which counts every slide the dataset
   *  hashed rather than the cohort matched in ``slide.csv``). */
  getProjectQcDecisions: (projectId: string, auth?: RequestAuthContext) =>
    request<ProjectQcDecisions>(`/projects/${projectId}/qc-decisions`, {}, auth),

  setProjectQcOverride: (
    projectId: string,
    flagId: string,
    body: ProjectQcOverrideRequest,
    auth?: RequestAuthContext,
  ) =>
    request<void>(
      `/projects/${projectId}/qc-overrides/${encodeURIComponent(flagId)}`,
      { method: 'POST', body: JSON.stringify(body) },
      auth,
    ),

  deleteProjectQcOverride: (projectId: string, flagId: string, auth?: RequestAuthContext) =>
    request<void>(
      `/projects/${projectId}/qc-overrides/${encodeURIComponent(flagId)}`,
      { method: 'DELETE' },
      auth,
    ),

  // ── PCS-345: evaluation export ────────────────────────────────────────────

  /** Download the evaluation run scores as CSV. */
  downloadEvaluationCsv: (runId: string, auth?: RequestAuthContext) =>
    requestBlob(`/runs/${encodeURIComponent(runId)}/evaluation/export?format=csv`, auth),

  /** Download the evaluation validation report as PDF. */
  downloadEvaluationPdf: (runId: string, auth?: RequestAuthContext) =>
    requestBlob(`/runs/${encodeURIComponent(runId)}/evaluation/export?format=pdf`, auth),

  // ── PCS-552: checklist export ─────────────────────────────────────────────

  /** Download the wizard pre-run checklist as a PDF. */
  downloadChecklistPdf: (runId: string, auth?: RequestAuthContext) =>
    requestBlob(`/runs/${encodeURIComponent(runId)}/checklist/export`, auth),

  // ── PCS-385: observability (SUPERADMIN only) ──────────────────────────────

  listActivity: (
    params: { status?: string; since?: string; limit?: number; cursor?: string; tenant_id?: string },
    auth?: RequestAuthContext,
  ) => {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.since) q.set('since', params.since);
    if (params.limit != null) q.set('limit', String(params.limit));
    if (params.cursor) q.set('cursor', params.cursor);
    if (params.tenant_id) q.set('tenant_id', params.tenant_id);
    const qs = q.toString();
    return request<import('./observability').ActivityListResponse>(
      `/admin/activity${qs ? `?${qs}` : ''}`,
      {},
      auth,
    );
  },

  getActivitySummary: (params: { tenant_id?: string } = {}, auth?: RequestAuthContext) => {
    const q = new URLSearchParams();
    if (params.tenant_id) q.set('tenant_id', params.tenant_id);
    const qs = q.toString();
    return request<import('./observability').ActivitySummary>(
      `/admin/observability/summary${qs ? `?${qs}` : ''}`,
      {},
      auth,
    );
  },

  cancelActivity: (activityId: string, auth?: RequestAuthContext) =>
    request<{ activity_id: string; status: string }>(
      `/admin/activity/${encodeURIComponent(activityId)}/cancel`,
      { method: 'POST' },
      auth,
    ),

  listBatchJobs: (
    params: { status?: string; page_size?: number; next_token?: string },
    auth?: RequestAuthContext,
  ) => {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.page_size != null) q.set('page_size', String(params.page_size));
    if (params.next_token) q.set('next_token', params.next_token);
    const qs = q.toString();
    return request<import('./observability').BatchJobListResponse>(
      `/admin/observability/batch/jobs${qs ? `?${qs}` : ''}`,
      {},
      auth,
    );
  },

  getBatchJobLogs: (
    jobId: string,
    params: { start_from_head?: boolean; next_token?: string; limit?: number; child_index?: number } = {},
    auth?: RequestAuthContext,
  ) => {
    const q = new URLSearchParams();
    if (params.start_from_head != null) q.set('start_from_head', String(params.start_from_head));
    if (params.next_token) q.set('next_token', params.next_token);
    if (params.limit != null) q.set('limit', String(params.limit));
    if (params.child_index != null) q.set('child_index', String(params.child_index));
    const qs = q.toString();
    return request<import('./observability').BatchJobLogsResponse>(
      `/admin/observability/batch/jobs/${encodeURIComponent(jobId)}/logs${qs ? `?${qs}` : ''}`,
      {},
      auth,
    );
  },

  getActivityLogs: (
    activityId: string,
    params: { start_from_head?: boolean; next_token?: string; limit?: number; child_index?: number } = {},
    auth?: RequestAuthContext,
  ) => {
    const q = new URLSearchParams();
    if (params.start_from_head != null) q.set('start_from_head', String(params.start_from_head));
    if (params.next_token) q.set('next_token', params.next_token);
    if (params.limit != null) q.set('limit', String(params.limit));
    if (params.child_index != null) q.set('child_index', String(params.child_index));
    const qs = q.toString();
    return request<import('./observability').BatchJobLogsResponse>(
      `/admin/activity/${encodeURIComponent(activityId)}/logs${qs ? `?${qs}` : ''}`,
      {},
      auth,
    );
  },

  getLogSources: (auth?: RequestAuthContext) =>
    request<import('./observability').LogSourcesResponse>(
      '/admin/observability/log-sources',
      {},
      auth,
    ),

  getLogs: (
    source: string,
    params: { stream?: string; start_from_head?: boolean; next_token?: string; limit?: number } = {},
    auth?: RequestAuthContext,
  ) => {
    const q = new URLSearchParams();
    q.set('source', source);
    if (params.stream) q.set('stream', params.stream);
    if (params.start_from_head != null) q.set('start_from_head', String(params.start_from_head));
    if (params.next_token) q.set('next_token', params.next_token);
    if (params.limit != null) q.set('limit', String(params.limit));
    return request<import('./observability').BatchJobLogsResponse>(
      `/admin/observability/logs?${q.toString()}`,
      {},
      auth,
    );
  },

  getOperationsSnapshot: (auth?: RequestAuthContext) =>
    request<import('./observability').OperationsSnapshot>(
      '/admin/operations/snapshot',
      {},
      auth,
    ),

  previewOperationsBatchCancel: (jobId: string, auth?: RequestAuthContext) =>
    request<OperationsBatchCancelPreview>(
      `/admin/operations/actions/batch/jobs/${encodeURIComponent(jobId)}/cancel/preview`,
      { method: 'POST' },
      auth,
    ),

  executeOperationsBatchCancel: (
    jobId: string,
    payload: { reason: string; confirmation: string },
    auth?: RequestAuthContext,
  ) =>
    request<OperationsBatchCancelResult>(
      `/admin/operations/actions/batch/jobs/${encodeURIComponent(jobId)}/cancel`,
      { method: 'POST', body: JSON.stringify(payload) },
      auth,
    ),
};
